import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { ChevronLeft, ChevronRight, Wallet, ArrowUpDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { timeTrackingAPI } from '../../lib/api';

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const fmtHours = (v) => {
  const n = Number(v || 0);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2).replace('.', ',')}`;
};

export const OvertimeBalancesCard = ({ branchId, locationId }) => {
  const [refDate, setRefDate] = useState(() => new Date());
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sortBySaldo, setSortBySaldo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        year: refDate.getFullYear(),
        month: refDate.getMonth() + 1,
      };
      if (locationId && locationId !== 'all') params.location_id = locationId;
      else if (branchId && branchId !== 'all') params.branch_id = branchId;
      const { data } = await timeTrackingAPI.getBalances(params);
      setBalances(data?.balances || []);
      setLoaded(true);
    } catch (err) {
      console.error('Stundenkonten laden fehlgeschlagen', err);
      toast.error('Fehler beim Laden der Stundenkonten');
    } finally {
      setLoading(false);
    }
  }, [refDate, branchId, locationId]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (dir) => {
    const d = new Date(refDate);
    d.setMonth(d.getMonth() + dir);
    setRefDate(d);
  };

  const sorted = useMemo(() => {
    const arr = [...balances];
    if (sortBySaldo) {
      arr.sort((a, b) => (a.cumulative_balance || 0) - (b.cumulative_balance || 0));
    } else {
      arr.sort((a, b) => (a.employee_name || '').localeCompare(b.employee_name || '', 'de'));
    }
    return arr;
  }, [balances, sortBySaldo]);

  const totals = useMemo(() => {
    const sum = balances.reduce((acc, b) => acc + (b.cumulative_balance || 0), 0);
    const plus = balances.filter((b) => (b.cumulative_balance || 0) > 0).length;
    const minus = balances.filter((b) => (b.cumulative_balance || 0) < 0).length;
    return { sum, plus, minus };
  }, [balances]);

  return (
    <Card className="border-border/60" data-testid="overtime-balances-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              Stundenkonten
            </CardTitle>
            <CardDescription>
              Kumulierter Über-/Minusstunden-Saldo je Mitarbeiter bis Ende des gewählten Monats
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="balances-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium px-1 min-w-[130px] text-center" data-testid="balances-month-label">
              {MONTHS[refDate.getMonth()]} {refDate.getFullYear()}
            </span>
            <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="balances-next-month">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="balances-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && !loaded ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
              <div className="p-2 sm:p-3 bg-secondary/30 rounded-lg text-center">
                <p className="text-[11px] sm:text-xs text-muted-foreground">Mitarbeiter</p>
                <p className="text-base sm:text-lg font-bold font-mono">{balances.length}</p>
              </div>
              <div className="p-2 sm:p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <p className="text-[11px] sm:text-xs text-muted-foreground">Mit Plus-Saldo</p>
                <p className="text-base sm:text-lg font-bold font-mono text-green-600">{totals.plus}</p>
              </div>
              <div className="p-2 sm:p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                <p className="text-[11px] sm:text-xs text-muted-foreground">Mit Minus-Saldo</p>
                <p className="text-base sm:text-lg font-bold font-mono text-red-600">{totals.minus}</p>
              </div>
            </div>

            <div className="rounded-lg border max-h-[480px] overflow-auto">
              <Table className="min-w-[640px]">
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>Mitarbeiter</TableHead>
                    <TableHead>Standort</TableHead>
                    <TableHead className="text-right">Wochenstd.</TableHead>
                    <TableHead className="text-right">Saldo {MONTHS[refDate.getMonth()].slice(0, 3)}.</TableHead>
                    <TableHead className="text-right">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-foreground"
                        onClick={() => setSortBySaldo((v) => !v)}
                        data-testid="balances-sort-toggle"
                      >
                        Gesamtsaldo <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        Keine Mitarbeiter im gewählten Bereich
                      </TableCell>
                    </TableRow>
                  ) : (
                    sorted.map((b) => (
                      <TableRow key={b.employee_id} data-testid={`balance-row-${b.employee_id}`}>
                        <TableCell className="font-medium">{b.employee_name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{b.location_name || '—'}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{b.weekly_hours}</TableCell>
                        <TableCell className={`text-right font-mono text-sm ${(b.month_overtime || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmtHours(b.month_overtime)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            className={`font-mono ${(b.cumulative_balance || 0) >= 0 ? 'bg-green-500 hover:bg-green-500' : 'bg-red-500 hover:bg-red-500'}`}
                            data-testid={`balance-total-${b.employee_id}`}
                          >
                            {fmtHours(b.cumulative_balance)} Std
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Saldo = fortlaufend addierte Über-/Minusstunden inkl. manuellem Startsaldo. Startwert &amp; Stichtag je
              Mitarbeiter im Reiter „Beschäftigung" pflegbar.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};
